import { motion } from "framer-motion";
import { Truck, FileText, CalendarDays, Bell, Plus, ArrowRight, Clock, AlertCircle, CheckCircle2, Sparkles, MoonStar } from "lucide-react";
import { NeedsAttention } from "./NeedsAttention";
import { TasksInbox } from "./TasksInbox";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const todayStr = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};

// Big, focused landing screen. Shows only what needs attention right now.
export const TodayView = ({
  currentUser,
  jobs = [],
  stagesDef = [],
  quotes = [],
  appointments = [],
  notices = [],
  needsAttention,
  tasks = [],
  onNavigate,
  onAddBooking,
  onAddQuote,
  onCreateJob,
  onCreateTask,
  onCompleteTask,
  onSnoozeTask,
  onDeleteTask,
  onOpenEndOfDay,
}) => {
  const inProgress = jobs.filter((j) => j.status === "in_progress");
  const newQuotes = quotes.filter((q) => q.status === "new");
  const today = todayStr();
  const todaysBookings = appointments.filter(
    (a) => a.preferred_date === today && a.status !== "cancelled"
  );
  const readyJobs = inProgress.filter((j) => j.current_stage === "ready");
  const highNotices = notices.filter((n) => n.priority === "high");

  const stageName = (job) =>
    stagesDef.find((s) => s.id === job.current_stage)?.name || "In progress";

  const firstName = (currentUser?.name || currentUser?.username || "there").split(" ")[0];

  const actions = [
    { label: "New Job", onClick: onCreateJob, testId: "today-new-job-btn", accent: "#22c55e" },
    { label: "Add Booking", onClick: onAddBooking, testId: "today-add-booking-btn", accent: "#3b82f6" },
    { label: "Add Quote", onClick: onAddQuote, testId: "today-add-quote-btn", accent: null },
  ];

  const cards = [
    {
      key: "jobs",
      title: "Jobs in progress",
      count: inProgress.length,
      icon: Truck,
      accent: "#3b82f6",
      go: () => onNavigate("jobs", "work"),
      items: inProgress.slice(0, 4).map((j) => ({
        id: j.id,
        primary: j.vehicle,
        secondary: `${j.customer_name} — ${stageName(j)}`,
      })),
      empty: "No jobs on the go right now",
    },
    {
      key: "quotes",
      title: "Quotes to action",
      count: newQuotes.length,
      icon: FileText,
      accent: "#f59e0b",
      go: () => onNavigate("quotes", "leads"),
      items: newQuotes.slice(0, 4).map((q) => ({
        id: q.id,
        primary: q.name,
        secondary: q.vehicle_model,
      })),
      empty: "All quotes handled — nice",
    },
    {
      key: "bookings",
      title: "Today's bookings",
      count: todaysBookings.length,
      icon: CalendarDays,
      accent: "#22c55e",
      go: () => onNavigate("appointments", "leads"),
      items: todaysBookings.slice(0, 4).map((a) => ({
        id: a.id,
        primary: a.name,
        secondary: `${a.preferred_time || ""} — ${a.vehicle_model || ""}`.trim(),
      })),
      empty: "Nothing booked in for today",
    },
  ];

  return (
    <div className="space-y-8" data-testid="today-view">
      {/* Greeting */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-body text-sm tracking-[0.15em] uppercase" style={{ color: "var(--dash-text-dim)" }}>
            {new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mt-1" style={{ color: "var(--dash-text)" }}>
            {greeting()}, {firstName}
          </h2>
        </div>
        <button onClick={onOpenEndOfDay} data-testid="wrap-up-day-btn"
          className="flex items-center gap-2 min-h-[44px] px-4 border font-body text-xs uppercase tracking-[0.1em] cursor-pointer transition-colors flex-shrink-0"
          style={{ borderColor: "rgba(139,92,246,0.4)", color: "#8b5cf6", backgroundColor: "rgba(139,92,246,0.08)" }}>
          <MoonStar size={16} /> <span className="hidden sm:inline">Wrap up day</span>
        </button>
      </div>

      {/* Ready-for-pickup nudge */}
      {readyJobs.length > 0 && (
        <button
          onClick={() => onNavigate("jobs", "work")}
          data-testid="today-ready-banner"
          className="w-full flex items-center gap-3 p-5 text-left border cursor-pointer transition-colors"
          style={{ backgroundColor: "rgba(34,197,94,0.08)", borderColor: "rgba(34,197,94,0.3)" }}
        >
          <CheckCircle2 size={22} className="text-[#22c55e] flex-shrink-0" />
          <span className="font-body text-sm md:text-base" style={{ color: "var(--dash-text)" }}>
            {readyJobs.length} {readyJobs.length === 1 ? "car is" : "cars are"} ready for pickup — chase the customer
          </span>
          <ArrowRight size={18} className="ml-auto text-[#22c55e] flex-shrink-0" />
        </button>
      )}

      {/* High priority notices */}
      {highNotices.length > 0 && (
        <div className="space-y-2">
          {highNotices.slice(0, 3).map((n) => (
            <div
              key={n.id}
              className="flex items-start gap-3 p-4 border"
              style={{ backgroundColor: "rgba(239,68,68,0.06)", borderColor: "rgba(239,68,68,0.3)" }}
            >
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                {n.title && <p className="font-body text-sm font-semibold" style={{ color: "var(--dash-text)" }}>{n.title}</p>}
                <p className="font-body text-sm" style={{ color: "var(--dash-text-muted)" }}>{n.message || n.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Primary actions — big, thumb-friendly */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {actions.map((a) => (
          <button
            key={a.label}
            onClick={a.onClick}
            data-testid={a.testId}
            className="flex items-center justify-center gap-2 min-h-[64px] px-6 font-heading text-sm md:text-base uppercase tracking-[0.12em] transition-all cursor-pointer border"
            style={
              a.accent
                ? { backgroundColor: a.accent, color: "#050505", borderColor: a.accent }
                : { backgroundColor: "transparent", color: "var(--dash-text)", borderColor: "var(--dash-border)" }
            }
          >
            <Plus size={20} /> {a.label}
          </button>
        ))}
      </div>

      {/* Needs attention */}
      {needsAttention && <NeedsAttention data={needsAttention} onNavigate={onNavigate} />}

      {/* Focus cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {cards.map((card, i) => {
          const Icon = card.icon;
          return (
            <motion.button
              key={card.key}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              onClick={card.go}
              data-testid={`today-card-${card.key}`}
              className="text-left border p-6 flex flex-col gap-4 cursor-pointer transition-colors hover:brightness-110"
              style={{ backgroundColor: "var(--dash-card)", borderColor: "var(--dash-border)" }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon size={18} style={{ color: card.accent }} />
                  <span className="font-body text-xs tracking-[0.15em] uppercase" style={{ color: "var(--dash-text-dim)" }}>
                    {card.title}
                  </span>
                </div>
                <ArrowRight size={16} style={{ color: "var(--dash-text-dim)" }} />
              </div>
              <span className="font-heading text-5xl leading-none" style={{ color: card.count > 0 ? card.accent : "var(--dash-text-dim)" }}>
                {card.count}
              </span>
              <div className="space-y-2 border-t pt-3" style={{ borderColor: "var(--dash-border)" }}>
                {card.items.length > 0 ? (
                  card.items.map((it) => (
                    <div key={it.id} className="min-w-0">
                      <p className="font-body text-sm truncate" style={{ color: "var(--dash-text)" }}>{it.primary}</p>
                      {it.secondary && (
                        <p className="font-body text-xs truncate" style={{ color: "var(--dash-text-muted)" }}>{it.secondary}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="font-body text-sm flex items-center gap-2" style={{ color: "var(--dash-text-dim)" }}>
                    <Sparkles size={13} /> {card.empty}
                  </p>
                )}
                {card.count > card.items.length && (
                  <p className="font-body text-xs pt-1" style={{ color: card.accent }}>
                    +{card.count - card.items.length} more
                  </p>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Brain-dump tasks */}
      <TasksInbox
        tasks={tasks}
        onCreate={onCreateTask}
        onComplete={onCompleteTask}
        onSnooze={onSnoozeTask}
        onDelete={onDeleteTask}
      />
    </div>
  );
};

export default TodayView;
